import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageShell } from '@/components/Layout';
import { Device } from '@/lib/device';
import { useToast } from '@/lib/ui';
import { useAuth } from '@/hooks/useAuth';
import { useConfirmedMutation } from '@/api/mutations';
import { fetchDbJobs, fetchDbJob, runDbJobFn, setDbJobScheduledFn } from '@/api/endpoints';
import { Btn, Section, StatCard } from '@/components/db/parts';
import type { DbJob, DbJobRun } from '@/api/types';

// Spec 05 §Polling: jobs refresh every minute.
const POLL_MS = 60_000;

// TimescaleDB names every job "<Policy label> [<job_id>]". The id is already its
// own column, so the suffix is pure noise in the table.
function jobLabel(j: DbJob): string {
  return j.application_name.replace(/\s*\[\d+\]\s*$/, '');
}

function fmtDuration(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const m = Math.floor(ms / 60_000);
  return `${m} min ${Math.round((ms % 60_000) / 1000)} s`;
}

function fmtWhen(iso: string | null): string {
  if (!iso) return 'jamais';
  return new Date(iso).toLocaleString('fr-FR');
}

/**
 * Two independent signals are folded into one badge:
 * job_status ('Scheduled' | 'Paused' | 'Running') is the scheduler's view, while
 * last_run_status ('Success' | 'Failed') describes the previous run. A paused job
 * whose last run failed is reported as paused — the scheduler state is what the
 * operator must act on first.
 */
function statusOf(j: DbJob): { label: string; cls: string; icon: string } {
  const s = (j.job_status ?? '').toLowerCase();
  if (s === 'paused') return { label: 'paused', cls: 'badge-amber', icon: '⏸' };
  if (s === 'running') return { label: 'running', cls: 'badge-blue', icon: '●' };
  if (j.last_run_status === 'Failed') return { label: 'failed', cls: 'badge-red', icon: '✗' };
  if (j.last_run_status === 'Success') return { label: 'success', cls: 'badge-green', icon: '✓' };
  return { label: 'jamais exécuté', cls: 'badge-slate', icon: '⏱' };
}

function isPaused(j: DbJob): boolean {
  return (j.job_status ?? '').toLowerCase() === 'paused';
}

function StatusBadge({ job }: { job: DbJob }) {
  const s = statusOf(job);
  return (
    <span className={`inline-flex items-center gap-1 ${s.cls} px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap`}>
      <span aria-hidden="true">{s.icon}</span> {s.label}
    </span>
  );
}

/**
 * Running a retention policy drops chunks past its retention window — the data
 * is gone. run_job on any other policy is heavy but reversible, so only
 * retention gets the destructive confirmation styling and wording.
 */
function isDestructive(j: DbJob): boolean {
  return j.proc_name.includes('retention');
}

export default function Jobs({ device }: { device: Device }) {
  // Selection lives in the URL so the browser back button restores it, matching Db.tsx.
  const [params, setParams] = useSearchParams();
  const selectedId = Number(params.get('job')) || null;
  const toast = useToast();
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const isSuperAdmin = me?.role === 'superAdmin';

  const { data, isLoading, isError, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ['db', 'jobs'],
    queryFn: fetchDbJobs,
    refetchInterval: POLL_MS,
  });
  const jobs = useMemo(() => data?.jobs ?? [], [data]);

  const invalidate = () => { void qc.invalidateQueries({ queryKey: ['db', 'jobs'] }); };

  const runMut = useConfirmedMutation(
    (j: DbJob) => runDbJobFn(j.job_id),
    {
      title: (j: DbJob) => `Exécuter ${jobLabel(j)}`,
      message: (j: DbJob) => isDestructive(j)
        ? `${j.proc_name} SUPPRIME des données au-delà de sa fenêtre de rétention. Cette opération est irréversible. Confirmer ?`
        : `Mettre ${j.proc_name}${j.hypertable ? ` sur ${j.hypertable}` : ''} en file d'exécution ? Le job est sérialisé avec l'ingestion.`,
      destructive: (j: DbJob) => isDestructive(j),
      onSuccess: (res) => { toast.push(`${res.message} (priorité ${res.priorite})`, 'success'); invalidate(); },
    },
  );

  const scheduleMut = useConfirmedMutation(
    ({ job, scheduled }: { job: DbJob; scheduled: boolean }) => setDbJobScheduledFn(job.job_id, scheduled),
    {
      title: ({ job, scheduled }: { job: DbJob; scheduled: boolean }) =>
        `${scheduled ? 'Reprendre' : 'Mettre en pause'} ${jobLabel(job)}`,
      message: ({ job, scheduled }: { job: DbJob; scheduled: boolean }) => scheduled
        ? `Réactiver la planification de ${job.proc_name} ?`
        : `Suspendre ${job.proc_name} ? Le job ne sera plus déclenché tant qu'il n'est pas repris`
          + `${job.proc_name.includes('compression') ? ' — l\'espace disque continuera de croître' : ''}.`,
      onSuccess: (res) => {
        toast.push(res.scheduled ? 'Job repris' : 'Job mis en pause', 'success');
        invalidate();
      },
    },
  );

  const busy = runMut.isPending || scheduleMut.isPending;

  const stats = useMemo(() => ({
    total: jobs.length,
    failing: jobs.filter((j) => j.last_run_status === 'Failed').length,
    paused: jobs.filter((j) => (j.job_status ?? '').toLowerCase() === 'paused').length,
    neverRan: jobs.filter((j) => j.total_runs === 0).length,
  }), [jobs]);

  if (isError) {
    return (
      <PageShell device={device} title="Jobs" breadcrumb="Serveur BD">
        <div className="p-8 text-center">
          <p className="text-destructive font-medium">Erreur de chargement</p>
          <p className="text-sm text-muted-foreground mt-1">{error instanceof Error ? error.message : 'Erreur inconnue'}</p>
        </div>
      </PageShell>
    );
  }

  if (isLoading) {
    return (
      <PageShell device={device} title="Jobs" breadcrumb="Serveur BD">
        <div className="p-8 text-muted-foreground">Chargement…</div>
      </PageShell>
    );
  }

  return (
    <PageShell device={device} title="Jobs" breadcrumb="Serveur BD">
      <div className="p-4 md:p-6 space-y-4">
        <Section
          title="Jobs TimescaleDB"
          action={
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground">
                MAJ: {dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString('fr-FR') : '—'}
              </span>
              <Btn onClick={() => void refetch()} disabled={isFetching}>
                {isFetching ? 'Refresh…' : 'Refresh'}
              </Btn>
            </div>
          }
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Jobs" value={stats.total} />
            <StatCard label="En échec" value={stats.failing} tone={stats.failing > 0 ? 'danger' : undefined} />
            <StatCard label="En pause" value={stats.paused} tone={stats.paused > 0 ? 'warn' : undefined} />
            <StatCard label="Jamais exécutés" value={stats.neverRan} />
          </div>
        </Section>

        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2.5">id</th>
                <th className="text-left px-4 py-2.5">job</th>
                <th className="text-left px-4 py-2.5">hypertable</th>
                <th className="text-left px-4 py-2.5">intervalle</th>
                <th className="text-left px-4 py-2.5">dernière exécution</th>
                <th className="text-left px-4 py-2.5">durée</th>
                <th className="text-left px-4 py-2.5">runs</th>
                <th className="text-left px-4 py-2.5">status</th>
                <th className="w-56 px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {jobs.map((j) => (
                <tr
                  key={j.job_id}
                  onClick={() => setParams({ job: String(j.job_id) }, { replace: true })}
                  className="cursor-pointer hover:bg-accent/30 select-none"
                >
                  <td className="px-4 py-2.5 font-mono text-xs">{j.job_id}</td>
                  <td className="px-4 py-2.5 text-xs">{jobLabel(j)}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{j.hypertable ?? '—'}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{j.schedule_interval}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{fmtWhen(j.last_run_started_at)}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{fmtDuration(j.last_run_duration_ms)}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">
                    {j.total_runs}
                    {j.total_failures > 0 && (
                      <span className="text-destructive"> · {j.total_failures} ✗</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5"><StatusBadge job={j} /></td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap space-x-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="text-xs text-primary hover:underline disabled:opacity-40"
                      disabled={busy}
                      onClick={() => scheduleMut.mutate({ job: j, scheduled: isPaused(j) })}
                    >
                      {isPaused(j) ? 'Reprendre' : 'Pause'}
                    </button>
                    <button
                      className={`text-xs hover:underline disabled:opacity-40 ${isDestructive(j) ? 'text-destructive' : 'text-primary'}`}
                      disabled={busy || !isSuperAdmin}
                      title={isSuperAdmin ? undefined : 'superAdmin requis'}
                      onClick={() => runMut.mutate(j)}
                    >
                      Run
                    </button>
                    <button
                      className="text-xs text-primary hover:underline"
                      onClick={() => setParams({ job: String(j.job_id) }, { replace: true })}
                    >
                      Historique →
                    </button>
                  </td>
                </tr>
              ))}
              {jobs.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground text-sm">Aucun job TimescaleDB.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-muted-foreground">
          Run est réservé aux superAdmin et passe par la queue node_climato (sérialisé avec l'ingestion).
          Les policies de rétention suppriment des données.
        </p>
      </div>

      {selectedId !== null && (
        <JobHistorySheet jobId={selectedId} onClose={() => setParams({}, { replace: true })} />
      )}
    </PageShell>
  );
}

function JobHistorySheet({ jobId, onClose }: { jobId: number; onClose: () => void }) {
  const { data: job, isLoading, isError, error } = useQuery({
    queryKey: ['db', 'jobs', jobId],
    queryFn: () => fetchDbJob(jobId),
  });

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full md:w-[720px] bg-card border-l border-border shadow-2xl overflow-y-auto flex flex-col">
        <div className="sticky top-0 bg-background/95 backdrop-blur border-b border-border px-5 py-3 flex items-start justify-between z-10">
          <div>
            <div className="text-xs text-muted-foreground">
              Job #{jobId}
              {job && <> · <code className="font-mono">{job.proc_name}</code>{job.hypertable && <> sur <code className="font-mono">{job.hypertable}</code></>}</>}
            </div>
            <h2 className="text-lg font-bold flex items-center gap-2 mt-0.5">
              Historique d'exécution {job && <StatusBadge job={job} />}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-muted" aria-label="Fermer">✕</button>
        </div>

        <div className="p-5 space-y-4">
          {isLoading && <div className="text-sm text-muted-foreground">Chargement…</div>}

          {isError && (
            <div className="text-sm">
              <p className="text-destructive font-medium">Erreur de chargement</p>
              <p className="text-muted-foreground mt-1">{error instanceof Error ? error.message : 'Erreur inconnue'}</p>
            </div>
          )}

          {job && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Exécutions" value={job.total_runs} />
                <StatCard label="Succès" value={job.total_successes} tone="success" />
                <StatCard label="Échecs" value={job.total_failures} tone={job.total_failures > 0 ? 'danger' : undefined} />
                <StatCard label="Prochaine" value={job.next_start ? new Date(job.next_start).toLocaleTimeString('fr-FR') : '—'} />
              </div>

              <dl className="text-xs grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                <dt className="text-muted-foreground">intervalle</dt><dd className="font-mono">{job.schedule_interval}</dd>
                <dt className="text-muted-foreground">procédure</dt><dd className="font-mono">{job.proc_name}</dd>
                <dt className="text-muted-foreground">dernière durée</dt><dd className="font-mono">{fmtDuration(job.last_run_duration_ms)}</dd>
                <dt className="text-muted-foreground">prochaine exéc.</dt><dd className="font-mono">{fmtWhen(job.next_start)}</dd>
              </dl>

              {job.config && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground">config</summary>
                  <pre className="mt-1 p-2 rounded-md bg-muted/50 overflow-x-auto font-mono">{JSON.stringify(job.config, null, 2)}</pre>
                </details>
              )}

              <div>
                <h3 className="text-sm font-semibold mb-2">20 dernières exécutions</h3>
                {!job.history_available ? (
                  <p className="text-xs text-muted-foreground">
                    Cette version de TimescaleDB n'expose pas <code className="font-mono">job_history</code>.
                  </p>
                ) : job.history.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Aucune exécution enregistrée — la politique de rétention de l'historique
                    (<code className="font-mono">policy_job_stat_history_retention</code>) a pu les purger.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {job.history.map((r) => <RunRow key={r.id} run={r} />)}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function RunRow({ run }: { run: DbJobRun }) {
  const failed = run.succeeded === false;
  return (
    <li className={`text-xs rounded-md border px-3 py-2 ${failed ? 'border-destructive/40 bg-destructive/5' : 'border-border'}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono">{fmtWhen(run.start_time)}</span>
        <span className="flex items-center gap-2 text-muted-foreground">
          {run.pid !== null && <span className="font-mono">pid {run.pid}</span>}
          <span className="font-mono">{fmtDuration(run.duration_ms)}</span>
          <span className={failed ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-500'}>
            {failed ? '✗ échec' : '✓ succès'}
          </span>
        </span>
      </div>
      {run.err_message && (
        <pre className="mt-1.5 whitespace-pre-wrap font-mono text-[11px] text-destructive">
          {run.sqlerrcode ? `[${run.sqlerrcode}] ` : ''}{run.err_message}
        </pre>
      )}
    </li>
  );
}
